import React, { useState } from 'react'
import { KeyRound, Copy, Check, Loader2, X, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '../../contexts/AuthContext'

const API_URL = process.env.REACT_APP_BACKEND_URL

// Bouton + modal : le Master Admin définit un nouveau mot de passe (généré ou saisi), affiché une seule fois
export default function ResetPasswordButton({ user }) {
  const { profile } = useAuth()
  const [open, setOpen] = useState(false)
  const [custom, setCustom] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)
  const [copied, setCopied] = useState(false)

  if (profile?.role !== 'master_admin') return null

  async function reset() {
    setBusy(true)
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${user.id}/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_user_id: profile.id, password: custom || null }),
      })
      const data = await res.json()
      if (!res.ok) { toast.error(data.detail || 'Erreur'); return }
      setResult(data)
      toast.success('Mot de passe réinitialisé')
    } catch { toast.error('Erreur réseau') }
    finally { setBusy(false) }
  }

  function close() { setOpen(false); setResult(null); setCustom(''); setCopied(false) }

  async function copy() {
    await navigator.clipboard.writeText(`${result.email} / ${result.temporary_password}`)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true) }}
        className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg"
        title="Réinitialiser le mot de passe"
        data-testid={`reset-password-${user.id}`}
      >
        <KeyRound className="w-4 h-4" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()} data-testid="reset-password-modal">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-bold text-slate-900 flex items-center gap-2"><KeyRound size={16} className="text-amber-600" /> Mot de passe</h3>
              <button onClick={close} className="text-slate-400 hover:text-slate-600" data-testid="reset-password-close"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-slate-600"><span className="font-medium text-slate-800">{user.prenom} {user.nom}</span><br /><span className="text-slate-500">{user.email}</span></p>
              {!result ? (
                <>
                  <p className="text-xs text-slate-500">Les mots de passe actuels ne sont pas lisibles (chiffrés). Vous définissez un nouveau mot de passe, affiché une seule fois ci-après.</p>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Nouveau mot de passe <span className="text-slate-400">(vide = généré automatiquement)</span></label>
                    <input value={custom} onChange={e => setCustom(e.target.value)} placeholder="Laisser vide pour générer"
                      className="w-full h-10 px-3 text-sm border border-slate-300 rounded-lg font-mono" data-testid="reset-password-input" />
                  </div>
                  <button onClick={reset} disabled={busy || (custom && custom.length < 8)}
                    className="w-full flex items-center justify-center gap-2 h-10 bg-amber-600 text-white text-sm font-semibold rounded-lg hover:bg-amber-700 disabled:opacity-50"
                    data-testid="reset-password-submit">
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                    {custom ? 'Appliquer ce mot de passe' : 'Générer et appliquer'}
                  </button>
                </>
              ) : (
                <>
                  <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4">
                    <p className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wide mb-1">Nouveau mot de passe — affiché une seule fois</p>
                    <p className="font-mono text-lg font-bold text-slate-900 select-all break-all" data-testid="reset-password-value">{result.temporary_password}</p>
                  </div>
                  <button onClick={copy} className="w-full flex items-center justify-center gap-2 h-10 border border-slate-300 text-sm font-medium rounded-lg hover:bg-slate-50" data-testid="reset-password-copy">
                    {copied ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
                    {copied ? 'Copié' : 'Copier identifiant + mot de passe'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
